(ns splash.ui.lender-portal.common.util
  (:require
    ["dayjs" :as dayjs]
    ["dayjs/plugin/timezone" :as dayjs-timezone]
    ["dayjs/plugin/utc" :as dayjs-utc]
    [clojure.string :as str]
    [splash.constants.splash-document-statuses.core :as splash-document-statuses]))

(.extend dayjs dayjs-utc)
(.extend dayjs dayjs-timezone)

(def ^:private eastern-zone "America/New_York")

(defn string->datetime
  "Parse an ISO-8601 datetime string (any fractional-second precision, 'Z' or an
   explicit offset) into a UTC dayjs instance. Throws if the string doesn't parse.
   dayjs treats a bare number as epoch millis, so non-strings are rejected here to
   match this fn's contract of parsing datetime strings, not arbitrary values."
  [date]
  (let [d (when (string? date) (.utc dayjs date))]
    (if (and d (.isValid d))
      d
      (throw (js/Error. (str "Invalid datetime: " date))))))

(defn try-parse-datetime
  "Same as `string->datetime`, but will swallow any exception encountered during parsing and return nil if failed to parse."
  [date]
  (try
    (string->datetime date)
    (catch js/Error _)))

;; dayjs's ISO parsing already handles any fractional-second precision (or none),
;; so there's no separate millisecond-specific or literal-zero-only parser needed
;; here anymore - these are kept as aliases for the sake of existing call sites.
(def datetime-ms-string->datetime string->datetime)
(def try-parse-datetime-ms try-parse-datetime)
(def datetime-zero-ms-string->datetime string->datetime)

(defn datetime->date-string
  "Format a dayjs instance as a yyyy-MM-dd string"
  [dt]
  (.format dt "YYYY-MM-DD"))

(defn datetime->datetime-string
  "Format a dayjs instance as a yyyy-MM-dd HH:mm:ss string"
  [dt]
  (.format dt "YYYY-MM-DD HH:mm:ss"))

(defn try-parse->datetime-string
  "Same as `datetime->datetime-string`, but will swallow any exception encountered during parsing and return nil if failed to parse."
  [date]
  (try
    (datetime->datetime-string date)
    (catch js/Error _)))

(defn ->to-local
  "Returns a dayjs instance with calendar fields corresponding to `dt`'s instant as
   observed in US Eastern time (accounting for daylight saving)."
  [dt]
  (.tz dt eastern-zone))

(defn try-parse->to-local
  "Same as `->to-local`, but will swallow any exception encountered during parsing and return nil if failed to parse."
  [date]
  (try
    (->to-local date)
    (catch js/Error _)))

(defn format-datetime-str
  "Parse a datetime string and format it as yyyy-MM-dd HH:mm:ss in Eastern time.
   Returns nil if parsing fails."
  [s]
  (some-> s
    try-parse-datetime
    try-parse->to-local
    try-parse->datetime-string))

(defn format-datetime-ms-str
  "Like format-datetime-str but tries millisecond format first before falling back to standard."
  [s]
  (some-> (or (try-parse-datetime-ms s) (try-parse-datetime s))
    try-parse->to-local
    try-parse->datetime-string))

(def product-type
  {:medical-resident-refi  1
   :student-loan-refinance 2
   :personal-loan          3})

(def suffix->type
  {".tiff" :image
   ".tif" :image
   ".jpg" :image
   ".jpeg" :image
   ".jfif" :image
   ".png" :image
   ".heic" :image
   ".doc" :doc
   ".docx" :docx
   ".txt" :text
   ".pdf" :pdf
   ".rtf" :text})

(def mimeType->type
  {"image/jpeg" :image
   "application/pdf" :pdf
   "text/plain" :text
   "application/msword" :doc
   "application/vnd.openxmlformats-officedocument.wordprocessingml.document" :docx})

(defn get-file-type
  "These correspond to the file extensions allowed in the document upload center"
  [url mimeType]
  (let [suffix (some->> url
                 (re-find #"\.\w*(?=$|\?)")
                 str/lower-case)]
    (or (get suffix->type suffix)
      (get mimeType->type mimeType))))

(defn deep-merge
  [a b]
  (merge-with (fn [x y]
                (cond (map? y) (deep-merge x y)
                  (vector? y) (concat x y)
                  :else y))
    a b))

(defn is-approved-document?
  [{:keys [splash-document-status-id]}]
  (splash-document-statuses/approved? splash-document-status-id))


(def initial-search-inputs
  {:application-id {:label     "Application ID"
                    :parse-fn  #(some-> % parse-long)
                    :value     nil
                    :validator (fn [v]
                                 (when (and (some? v)
                                         (let [s (str v)]
                                           (not (and (re-matches #"^\d+$" s)
                                                  (<= 3 (count s) 9)))))
                                   "Invalid Application ID format"))}
   :first-name     {:label "First Name"
                    :value nil}
   :last-name      {:label "Last Name"
                    :value nil}})

(comment

  (string->datetime "2024-11-06T17:49:09.197Z")

  )
